import Papa from 'papaparse';

/**
 * Load Asset List CSV file
 * Note: Asset_List.csv uses semicolon delimiter
 */
export const loadAssetList = async (filePath = '/Asset_List.csv') => {
  try {
    const response = await fetch(filePath);
    const text = await response.text();
    
    // Use papaparse with semicolon delimiter
    return new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        delimiter: ';', // Asset_List.csv uses semicolon
        complete: (results) => {
          resolve(results.data);
        },
        error: (error) => {
          reject(error);
        }
      });
    });
  } catch (error) {
    console.error(`Error loading Asset List from ${filePath}:`, error);
    throw error;
  }
};

/**
 * Create a lookup map from Asset List
 * Maps by Asset Code and Alt Name (registration)
 */
export const createAssetLookup = (assetListData) => {
  const lookup = {
    byAssetCode: {},
    byAltName: {}
  };
  
  assetListData.forEach(asset => {
    const assetCode = (asset['Asset Code'] || '').trim();
    const altName = (asset['Alt Name'] || '').trim();
    
    if (assetCode) {
      lookup.byAssetCode[assetCode] = asset;
    }
    
    if (altName && altName !== '--') {
      lookup.byAltName[altName] = asset;
    }
  });
  
  return lookup;
};

/**
 * Match equipment with asset data
 */
export const enrichEquipmentWithAssetData = (equipment, assetLookup) => {
  return equipment.map(eq => {
    // Try to match by Unit No (Asset Code)
    let assetData = assetLookup.byAssetCode[eq.unitNo];
    
    // If not found, try to match by Registration (Alt Name)
    if (!assetData && eq.registration) {
      assetData = assetLookup.byAltName[eq.registration];
    }
    
    if (assetData) {
      const assetSubType = (assetData['Asset Sub Type'] || '').trim() || null;
      // Extract Asset Type from Asset Sub Type (first part before any specific details)
      // For example: "Low Floor Tarmac Coach" -> "Tarmac Coach" or keep as is
      let assetType = assetSubType;
      if (assetSubType) {
        // Try to extract a more general type (remove specific details)
        // This is a simple approach - can be refined based on actual data patterns
        const parts = assetSubType.split(' ');
        if (parts.length > 2) {
          // Take last 2-3 words as type
          assetType = parts.slice(-2).join(' ');
        }
      }
      
      return {
        ...eq,
        assetModel: (assetData['Asset Model'] || '').trim() || null,
        assetType: assetType,
        assetSubType: assetSubType,
        location: (assetData['Location'] || '').trim() || null,
        subLocation: (assetData['Sub Location'] || '').trim() || null,
        assetCode: (assetData['Asset Code'] || '').trim() || null
      };
    }
    
    // Return equipment with null values if no match found
    return {
      ...eq,
      assetModel: null,
      assetType: null,
      assetSubType: null,
      location: null,
      subLocation: null,
      assetCode: null
    };
  });
};

/**
 * Load asset list and enrich equipment data
 */
export const loadAndEnrichEquipment = async (equipment) => {
  try {
    const assetListData = await loadAssetList('/Asset_List.csv');
    const assetLookup = createAssetLookup(assetListData);
    const enrichedEquipment = enrichEquipmentWithAssetData(equipment, assetLookup);
    return enrichedEquipment;
  } catch (error) {
    console.error('Error loading and enriching equipment:', error);
    // Return original equipment if asset list loading fails
    return equipment.map(eq => ({
      ...eq,
      assetModel: null,
      assetType: null,
      assetSubType: null,
      location: null,
      subLocation: null,
      assetCode: null
    }));
  }
};

